import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, ShieldCheck, ArrowRight, Plus, Minus, Trash2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/v1/merch-hub`;

/**
 * BYWE Merch Hub v2 — multi-size, multi-quantity per product.
 * Route: /team/:teamSlug
 */
const TeamMerchHub = () => {
  const params = useParams();
  const teamSlug = params.teamSlug || 'bywe';

  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // Selection state — { [productId]: [{size, qty}, ...] }
  const [selections, setSelections] = useState({});
  const [playerName, setPlayerName] = useState('');
  const [parentName, setParentName] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/${teamSlug}/catalog`);
        if (!active) return;
        // Absolutize backend-relative asset URLs so they resolve against the
        // backend host (not the frontend origin) — required for cross-origin
        // setups and for older Safari to use a clean ASCII URL.
        const absolutize = (p) => (p && p.startsWith('/') ? `${BACKEND}${p}` : p);
        const data = res.data;
        data.logo = absolutize(data.logo);
        data.hero = absolutize(data.hero);
        if (Array.isArray(data.items)) {
          data.items = data.items.map((i) => ({ ...i, image: absolutize(i.image) }));
        }
        setCatalog(data);
      } catch (err) {
        if (active) toast.error('Team not found.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [teamSlug]);

  const sizes = catalog?.sizes || ['YS', 'YM', 'YL', 'AS', 'AM', 'AL', 'AXL'];

  // ===== Row helpers =====
  const addRow = (productId) => {
    setSelections((prev) => {
      const rows = prev[productId] || [];
      return { ...prev, [productId]: [...rows, { size: 'AM', qty: 1 }] };
    });
  };

  const removeRow = (productId, idx) => {
    setSelections((prev) => {
      const rows = (prev[productId] || []).filter((_, i) => i !== idx);
      const next = { ...prev };
      if (rows.length === 0) delete next[productId];
      else next[productId] = rows;
      return next;
    });
  };

  const updateRow = (productId, idx, patch) => {
    setSelections((prev) => {
      const rows = [...(prev[productId] || [])];
      rows[idx] = { ...rows[idx], ...patch };
      return { ...prev, [productId]: rows };
    });
  };

  const decrementQty = (productId, idx) => {
    const cur = selections[productId]?.[idx];
    if (!cur) return;
    if (cur.qty <= 1) {
      removeRow(productId, idx);
    } else {
      updateRow(productId, idx, { qty: cur.qty - 1 });
    }
  };

  const incrementQty = (productId, idx) => {
    const cur = selections[productId]?.[idx];
    if (!cur) return;
    updateRow(productId, idx, { qty: Math.min(99, cur.qty + 1) });
  };

  // ===== Totals =====
  const { total, totalUnits, perProduct } = useMemo(() => {
    if (!catalog) return { total: 0, totalUnits: 0, perProduct: {} };
    let t = 0;
    let u = 0;
    const pp = {};
    catalog.items.forEach((it) => {
      const rows = selections[it.token] || [];
      const sub = rows.reduce((s, r) => s + it.price * (r.qty || 0), 0);
      const qty = rows.reduce((s, r) => s + (r.qty || 0), 0);
      pp[it.token] = { subtotal: sub, qty };
      t += sub;
      u += qty;
    });
    return { total: t, totalUnits: u, perProduct: pp };
  }, [catalog, selections]);

  const validate = () => {
    if (totalUnits === 0) return 'Add at least one size + quantity.';
    if (!playerName.trim()) return 'Player Name is required.';
    if (!parentName.trim()) return 'Parent Name is required.';
    if (parentPhone.trim().length < 7) return 'Valid parent phone is required.';
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const products = Object.entries(selections)
        .filter(([, rows]) => rows.length > 0)
        .map(([product_id, rows]) => ({
          product_id,
          selections: rows.filter((r) => r.qty > 0).map((r) => ({ size: r.size, quantity: r.qty })),
        }))
        .filter((p) => p.selections.length > 0);

      const payload = {
        team_slug: teamSlug,
        player_name: playerName.trim(),
        parent_name: parentName.trim(),
        parent_phone: parentPhone.trim(),
        parent_email: parentEmail.trim() || null,
        products,
        client_total: total,
      };
      const res = await axios.post(`${API}/order`, payload);
      setSuccess(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to submit order.';
      toast.error(typeof detail === 'string' ? detail : 'Failed to submit order.');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Success Screen =====
  if (success) {
    const payment = catalog?.payment;
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[520px] border-2 border-orange-500/50 p-10" data-testid="merch-success">
          <div className="flex items-center justify-center w-14 h-14 mx-auto mb-6 bg-orange-500 text-black">
            <Check className="w-8 h-8" />
          </div>
          <div className="text-[10px] font-bold tracking-[0.4em] text-orange-500 text-center uppercase">
            Order Successfully Reserved
          </div>
          <div className="text-[10px] font-bold tracking-[0.4em] text-gray-500 text-center uppercase mt-2">
            // Coach Notified
          </div>
          <div className="mt-10 border-t border-gray-800 pt-6 space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Order ID</span><span className="font-mono text-xs">{success.order_id?.slice(0, 13)}…</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Total Units</span><span className="font-mono font-bold">{success.total_units}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Total Locked</span><span className="font-mono font-bold text-orange-500">${success.verified_total.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="text-green-400 font-bold uppercase tracking-widest text-xs">Reserved</span></div>
          </div>

          {/* Payment Instructions */}
          {payment && (
            <div className="mt-8 border-2 border-orange-500" data-testid="merch-payment-block">
              <div className="bg-orange-500 text-black px-4 py-2 text-[10px] font-bold tracking-[0.3em] uppercase">
                Payment Instructions
              </div>
              <div className="p-5 bg-[#0a0a0a]">
                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Send Payment To</div>
                <div className="text-lg font-black text-white mt-1" data-testid="merch-payment-recipient">{payment.recipient_name}</div>

                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="border border-gray-800 p-3">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-orange-500 font-bold">Venmo</div>
                    <div className="font-mono font-bold text-white text-sm mt-1" data-testid="merch-payment-venmo">{payment.venmo}</div>
                  </div>
                  <div className="border border-gray-800 p-3">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-orange-500 font-bold">Cash App</div>
                    <div className="font-mono font-bold text-white text-sm mt-1" data-testid="merch-payment-cashapp">{payment.cashapp}</div>
                  </div>
                </div>

                <div className="mt-4 text-[11px] text-gray-500 leading-relaxed">
                  Include the player's name in the payment note so the team can match it to your reservation.
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => {
              setSuccess(null);
              setSelections({});
              setPlayerName(''); setParentName(''); setParentPhone(''); setParentEmail('');
            }}
            className="mt-10 w-full bg-white text-black py-4 font-black text-xs tracking-[0.25em] uppercase hover:bg-orange-500 transition-colors"
            data-testid="merch-new-order-btn"
          >
            Submit Another Order
          </button>
        </div>
      </div>
    );
  }

  // ===== Main Form =====
  return (
    <div className="min-h-screen bg-black text-white py-16 px-4">
      <div className="mx-auto w-full max-w-[520px]" data-testid="merch-container">
        {loading ? (
          <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : !catalog ? (
          <div className="text-center text-gray-500 py-32">Team catalog unavailable.</div>
        ) : (
          <>
            {/* Floating Logo — top center */}
            {catalog.logo && (
              <div className="flex justify-center mb-6" data-testid="merch-floating-logo">
                <div className="relative">
                  <div className="absolute inset-0 bg-orange-500/20 blur-2xl rounded-full" />
                  <img
                    src={catalog.logo}
                    alt={`${catalog.team_label} logo`}
                    className="relative h-24 sm:h-28 w-auto object-contain drop-shadow-[0_8px_24px_rgba(249,115,22,0.35)]"
                    data-testid="merch-logo"
                  />
                </div>
              </div>
            )}

            {/* Collection Title — under logo */}
            <div className="text-center mb-8">
              <div className="text-[10px] font-bold tracking-[0.4em] text-orange-500 uppercase">
                Team Merch Hub
              </div>
              <h1
                className="mt-2 text-2xl sm:text-3xl font-black uppercase tracking-tight leading-[1.05]"
                data-testid="merch-title"
              >
                Official <span className="text-orange-500">{catalog.team_label.split(' ')[0]}</span> Player Collection
              </h1>
            </div>

            {/* Hero Image — under title */}
            {catalog.hero && (
              <div
                className="mb-10 relative -mx-4 sm:mx-0 overflow-hidden border-2 border-orange-500/40"
                data-testid="merch-hero"
              >
                <div className="relative h-56 sm:h-72 bg-black">
                  <img
                    src={catalog.hero}
                    alt={`${catalog.team_label} hero`}
                    className="absolute inset-0 w-full h-full object-cover opacity-85"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                </div>
                {catalog.tagline && (
                  <div className="absolute bottom-0 left-0 right-0 px-5 py-4 sm:px-6 sm:py-5">
                    <p
                      className="text-xs sm:text-sm text-white/95 leading-snug font-medium"
                      data-testid="merch-tagline"
                    >
                      {catalog.tagline}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="mb-12 h-px bg-gradient-to-r from-orange-500/60 via-white/10 to-transparent" />

            {/* Product Matrix */}
            <div className="space-y-3 mb-10" data-testid="merch-items">
              {catalog.items.map((item) => {
                const rows = selections[item.token] || [];
                const hasRows = rows.length > 0;
                const lineSubtotal = perProduct[item.token]?.subtotal || 0;

                return (
                  <div
                    key={item.token}
                    className={`border-2 transition-all ${hasRows ? 'border-orange-500 bg-orange-500/[0.04]' : 'border-gray-800 bg-[#0a0a0a]'}`}
                    data-testid={`merch-item-${item.token}`}
                  >
                    {/* Product header */}
                    <div className="p-5 border-b border-white/5">
                      {item.image && (
                        <div className="mb-4 -mx-5 -mt-5 bg-black overflow-hidden border-b border-white/5">
                          <img src={item.image} alt={item.name} className="w-full h-44 object-contain" loading="lazy" />
                        </div>
                      )}
                      <div className="flex items-baseline justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-black text-base uppercase tracking-tight leading-tight">{item.name}</h3>
                          {item.subtitle && <div className="text-[11px] text-gray-500 uppercase tracking-widest mt-1">{item.subtitle}</div>}
                        </div>
                        <div className="text-lg font-black font-mono text-orange-500 whitespace-nowrap">${item.price.toFixed(2)}</div>
                      </div>
                    </div>

                    {/* Selection rows */}
                    <div className="px-5 py-4 space-y-2">
                      {rows.map((row, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2"
                          data-testid={`merch-row-${item.token}-${idx}`}
                        >
                          <select
                            value={row.size}
                            onChange={(e) => updateRow(item.token, idx, { size: e.target.value })}
                            className="flex-1 bg-black border-2 border-gray-800 text-white px-3 py-2 font-mono text-sm focus:border-orange-500 outline-none uppercase"
                            data-testid={`merch-row-size-${item.token}-${idx}`}
                          >
                            {sizes.map((s) => (
                              <option key={s} value={s}>{s} · {catalog.size_labels?.[s] || s}</option>
                            ))}
                          </select>

                          {/* Qty counter */}
                          <div className="flex items-center border-2 border-gray-800">
                            <button
                              onClick={() => decrementQty(item.token, idx)}
                              className="w-8 h-9 flex items-center justify-center hover:bg-gray-800 text-orange-500"
                              data-testid={`merch-row-dec-${item.token}-${idx}`}
                              type="button"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <input
                              type="number"
                              min="0"
                              max="99"
                              value={row.qty}
                              onChange={(e) => {
                                const v = parseInt(e.target.value || '0', 10);
                                if (Number.isNaN(v) || v <= 0) removeRow(item.token, idx);
                                else updateRow(item.token, idx, { qty: Math.min(99, v) });
                              }}
                              className="w-10 text-center bg-black text-white font-mono text-sm py-2 outline-none"
                              data-testid={`merch-row-qty-${item.token}-${idx}`}
                            />
                            <button
                              onClick={() => incrementQty(item.token, idx)}
                              className="w-8 h-9 flex items-center justify-center hover:bg-gray-800 text-orange-500"
                              data-testid={`merch-row-inc-${item.token}-${idx}`}
                              type="button"
                              aria-label="Increase quantity"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>

                          {/* Remove row */}
                          <button
                            onClick={() => removeRow(item.token, idx)}
                            className="w-9 h-9 flex items-center justify-center border-2 border-gray-800 text-gray-600 hover:text-red-500 hover:border-red-500/50 transition-colors"
                            data-testid={`merch-row-remove-${item.token}-${idx}`}
                            type="button"
                            aria-label="Remove row"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}

                      {/* Add size button — asymmetric inline */}
                      <button
                        onClick={() => addRow(item.token)}
                        className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] border transition-colors ${hasRows ? 'border-orange-500/40 text-orange-500 hover:bg-orange-500 hover:text-black' : 'border-gray-700 text-gray-400 hover:border-orange-500 hover:text-orange-500'}`}
                        data-testid={`merch-add-row-${item.token}`}
                        type="button"
                      >
                        <Plus className="w-3 h-3" />
                        {hasRows ? 'Add Another Size' : 'Add Size'}
                      </button>

                      {/* Line subtotal */}
                      {hasRows && (
                        <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/5">
                          <span className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Line Subtotal</span>
                          <span className="font-mono font-bold text-sm">${lineSubtotal.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Customer fields */}
            <div className="space-y-3 mb-10" data-testid="merch-form">
              <Field label="Player Name" value={playerName} onChange={setPlayerName} testId="merch-player-name" />
              <Field label="Parent Name" value={parentName} onChange={setParentName} testId="merch-parent-name" />
              <Field label="Parent Phone Number" value={parentPhone} onChange={setParentPhone} type="tel" testId="merch-parent-phone" />
              <Field label="Parent Email (Optional)" value={parentEmail} onChange={setParentEmail} type="email" testId="merch-parent-email" />
            </div>

            {/* Summary */}
            <div className="border-2 border-white p-5 mb-6" data-testid="merch-summary">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-500">Order Total</div>
                <div className="text-[9px] uppercase tracking-widest text-orange-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Server-Verified</div>
              </div>
              <div className="flex items-baseline justify-between">
                <div className="text-[11px] text-gray-500">{totalUnits} unit{totalUnits !== 1 ? 's' : ''}</div>
                <div className="text-4xl font-black font-mono" data-testid="merch-total">${total.toFixed(2)}</div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={submit}
              disabled={submitting || totalUnits === 0}
              className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-gray-800 disabled:text-gray-600 text-black py-5 font-black text-sm tracking-[0.2em] uppercase transition-colors flex items-center justify-center gap-2"
              data-testid="merch-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Reserving</>
              ) : (
                <>Submit Team Order Reservation <ArrowRight className="w-5 h-5" /></>
              )}
            </button>

            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-700 text-center mt-6">
              Reservation only · Payment collected by team
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', testId }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full bg-black border-2 border-gray-800 px-3 py-3 text-white font-mono text-sm focus:border-orange-500 outline-none"
      data-testid={testId}
    />
  </div>
);

export default TeamMerchHub;
